import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from './AuthContext';
import Login from './Login';
import LanguageLearningPlayer from './LanguageLearningPlayer';
import SubscribePage from './SubscribePage';
import OnboardingScreen from './OnboardingScreen';
import { getUserDarkModeSetting } from './firestore';
import type { AppearanceMode } from './firestore';
import { db } from './firebaseConfig';
import { doc, onSnapshot, collection, query, limit, getDocs } from 'firebase/firestore';
import tracksData from './tracks.json';


const App: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const [appearance, setAppearance] = useState<AppearanceMode>(() => {
    const stored = localStorage.getItem('appearance') as AppearanceMode | null;
    if (stored === 'system' || stored === 'light' || stored === 'dark') return stored;
    // Migrate from old boolean key
    const oldDark = localStorage.getItem('darkMode');
    if (oldDark !== null) return oldDark === 'true' ? 'dark' : 'light';
    return 'system';
  });
  const [systemDark, setSystemDark] = useState(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const darkMode = appearance === 'dark' || (appearance === 'system' && systemDark);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [isSubscriber, setIsSubscriber] = useState(false);
  // Always show for now — will gate with localStorage before launch
  const [showOnboarding, setShowOnboarding] = useState(true);

  // Keep browser chrome (status bar + address bar) in sync with dark mode.
  // theme-color affects the top status bar; color-scheme affects Safari's bottom toolbar.
  useEffect(() => {
    const color = darkMode ? '#111827' : '#ffffff';
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', color);
    document.documentElement.style.colorScheme = darkMode ? 'dark' : 'light';
  }, [darkMode]);

  // Listen for OS-level dark mode changes when appearance is 'system'
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const loadDarkModeSetting = async () => {
      if (user) {
        const savedAppearance = await getUserDarkModeSetting(user.uid);
        if (savedAppearance !== null) {
          setAppearance(savedAppearance);
          localStorage.setItem('appearance', savedAppearance);
        }
      }
      setIsLoading(false);
    };
    loadDarkModeSetting();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setHasAccess(null);
      setIsSubscriber(false);
      return;
    }
    const unsub = onSnapshot(doc(db, 'userAccess', user.uid), async (snap) => {
      const access = snap.exists() ? snap.data()?.hasAccess === true : false;
      setHasAccess(access);
      if (access) {
        try {
          const subsSnap = await getDocs(
            query(collection(db, 'customers', user.uid, 'subscriptions'), limit(1))
          );
          setIsSubscriber(!subsSnap.empty);
        } catch {
          setIsSubscriber(false);
        }
      } else {
        setIsSubscriber(false);
      }
    });
    return unsub;
  }, [user]);

  const handleAppearanceChange = (a: AppearanceMode) => {
    setAppearance(a);
    localStorage.setItem('appearance', a);
    if (user) {
      import('./firestore').then(({ saveUserDarkModeSetting }) => {
        saveUserDarkModeSetting(user.uid, a);
      });
    }
  };

  const dismissOnboarding = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setShowOnboarding(false);
  };

  if (!user) return <Login />;

  if (isLoading || hasAccess === null) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (hasAccess === false) return <SubscribePage darkMode={darkMode} />;

  if (showOnboarding) return <OnboardingScreen onDismiss={dismissOnboarding} />;

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-black'}`}>
      <div className="max-w-2xl mx-auto">
        <LanguageLearningPlayer
          tracks={tracksData}
          artworkUrl="/images/title_art.png"
          darkMode={darkMode}
          appearance={appearance}
          onAppearanceChange={handleAppearanceChange}
          isSubscriber={isSubscriber}
        />
      </div>
    </div>
  );
};

export default App;
